import {
	faExclamationCircle,
	faExclamationTriangle,
} from '@fortawesome/pro-solid-svg-icons';
import { useLocationDatesValidation } from '@uturn/portal/modules/shipments/validation';
import { TransportTypeCode } from '@uturn/portal/types/shipment';
import { formatPrice, formatWeight } from '@uturn/portal/utils';
import { formatSuggestion } from '@uturn/portal/utils/format-address-lookup-suggestions';
import type {
	CellValueChangedEvent,
	ColumnEvent,
	CustomCellEditorProps,
	CustomCellRendererProps,
	GridOptions,
	ITooltipParams,
	MultiSelectOptionType,
	ValueFormatterParams,
	ValueGetterParams,
	ValueSetterParams,
} from '@uturn/ui-kit';
import {
	AgGridReact,
	DATETIME_HINT,
	Icon,
	multiSelectValueAsString,
} from '@uturn/ui-kit';
import { formatDatetime } from '@uturn/utilities/browser';
import dayjs from 'dayjs';
import equal from 'fast-deep-equal';
import { forwardRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isColDef, isColGroupDef } from '../shipments-table/typeguard';
import CurrencyCellEditor from './components/currency-editor';
import DateTimePickerEditor from './components/date-time-picker-editor';
import LocationAutoCompleteEditor from './components/location-auto-complete-editor';
import MultiSelectEditor from './components/multi-select-editor';
import type { Item } from './components/select-editor';
import SelectEditor from './components/select-editor';
import WeightCellEditor from './components/weight-editor';
import useColumns from './hooks/use-columns';
import useShipmentTableOptions from './hooks/use-shipment-table-options';
import {
	CONTAINER_NUMBER_FIELD,
	validateContainerNumberDuplicatesOnCellChange,
} from './utils/container-numbers';
import { getDefaultRowData } from './utils/default-row-data';
import {
	matchLocationField,
	toLocationActionsFromRow,
} from './utils/locations';

const GRID_ROW_COUNT = 50;

const getTermsAndConditions = (locations) => {
	const locationCountryCodes = Object.values(locations)
		.filter((location) => location?.country?.countryCode)
		.map((location) => location?.country?.countryCode);

	if (locationCountryCodes.length === 0) {
		// Reset to default data when there are no (longer any) locations
		return {
			value: 'avc',
			label: 'AVC',
		};
	}

	const isCrossBorderShipment = locationCountryCodes.some(
		(countryCode) => countryCode !== 'NLD',
	);
	const isGBRShipment = locationCountryCodes.every(
		(countryCode) => countryCode === 'GBR',
	);
	switch (true) {
		case isGBRShipment:
			return {
				value: 'rha',
				label: 'RHA',
			};
		case isCrossBorderShipment:
			return {
				value: 'cmr',
				label: 'CMR',
			};
		default:
			return {
				value: 'avc',
				label: 'AVC',
			};
	}
};

const isDirty = (newData: any, defaultData: any) => {
	const { id, dirty, ...data } = newData;
	const filteredData = Object.entries(data).reduce((acc, [key, value]) => {
		// Deleting a value in the table, will set the cell to an empty string
		if (value.value !== undefined && value.value !== '') {
			// We only wanna compare the actual values not any other properties, so we implicity only se tthe value property
			acc[key] = { value: value.value };
		}
		return acc;
	}, {});

	return !equal(filteredData, defaultData);
};

const getSelectColumnDefaults = (values: Item[]) => ({
	cellEditor: (params: CustomCellEditorProps) => (
		<SelectEditor {...params} values={values} />
	),
	cellRenderer: (params: CustomCellRendererProps) => {
		const colId = params.column?.getColId();
		if (!colId) {
			return <span>{params.value?.label}</span>;
		}

		const data = params.data[colId];
		return (
			<>
				<span className="pr-2">{params.value?.label}</span>
				{data?.isValid === false && (
					<Icon
						className="absolute bottom-2/4 right-3 translate-y-2/4 text-red-500"
						icon={faExclamationCircle}
					/>
				)}
			</>
		);
	},
});

const getMultiSelectColumnDefaults = (options: MultiSelectOptionType[]) => ({
	valueFormatter: (params: ValueFormatterParams) => params.value,
	cellRenderer: (params: CustomCellRendererProps) => {
		const colValue = multiSelectValueAsString(options, params.value);
		const colId = params.column?.getColId();

		if (!colId) {
			return <span>{colValue}</span>;
		}

		const data = params.data[colId];

		return (
			<>
				<span className="pr-2">{colValue}</span>
				{data?.isValid === false && (
					<Icon
						className="absolute bottom-2/4 right-3 translate-y-2/4 text-red-500"
						icon={faExclamationCircle}
					/>
				)}
			</>
		);
	},
	cellEditor: (params: CustomCellEditorProps) => (
		<MultiSelectEditor {...params} options={options} />
	),
});

const useLocationAutoCompleteDefaults = () => {
	const getLocationAutoCompleteDefaults = () => ({
		minWidth: 250,
		cellEditor: (params: CustomCellEditorProps) => (
			<LocationAutoCompleteEditor {...params} />
		),
		cellRenderer: (params: CustomCellRendererProps) => {
			const colId = params.column?.getColId();
			if (!colId) {
				return <span>{formatSuggestion(params.data[colId].value)}</span>;
			}

			const data = params.data[colId];
			return (
				<>
					<span className="pr-2">
						{params.data[colId]?.value &&
							formatSuggestion(params.data[colId].value)}
					</span>
					{data?.isValid === false && (
						<Icon
							className="absolute bottom-2/4 right-3 translate-y-2/4 text-red-500"
							icon={faExclamationCircle}
						/>
					)}
				</>
			);
		},
	});

	return { getLocationAutoCompleteDefaults };
};

type Props = {
	transportType?: TransportTypeCode;
};

const MultiCreateTable = forwardRef<AgGridReact, Props>(
	({ transportType = TransportTypeCode.IMPORT }, ref) => {
		const { t } = useTranslation();
		const { columns, requiredColumns } = useColumns(transportType);
		const {
			containerTypes,
			shippingLines,
			requirements,
			locationTypes,
			termsAndConditions,
			carriers,
			userSettings,
			isPending,
		} = useShipmentTableOptions();

		const defaultRowData = getDefaultRowData(
			transportType,
			userSettings,
			shippingLines,
		);
		const { getLocationAutoCompleteDefaults } =
			useLocationAutoCompleteDefaults();
		const { validateLocationDates } = useLocationDatesValidation();

		// Live validation: when a `from` / `until` cell changes, re-run the shared
		// cross-location validator on the whole row so users see issues without
		// waiting for submit. Mirrors manual create's `mode: 'onBlur'` UX for date
		// fields.
		const validateRowDatesOnCellChange = useCallback(
			(event: CellValueChangedEvent) => {
				const changedColumnId = event.column.getColId();
				if (
					!matchLocationField(changedColumnId, 'from') &&
					!matchLocationField(changedColumnId, 'until')
				) {
					return;
				}

				const locations = toLocationActionsFromRow(event.data);
				// Run live validation as if publishing so the user sees all rules
				// (including pickup-future). The submit handler still passes the real
				// `publish` flag, so saving as concept never actually blocks.
				const validationResults = validateLocationDates(
					locations,
					'',
					true,
					true,
				);

				const errorMessageByCellId = new Map<string, string>();
				if (!validationResults.valid) {
					for (const { path, message } of validationResults.issues) {
						const pathMatch = String(path).match(
							/^locations\.(\d+)\.action\.(dateFrom|dateUntil)$/,
						);
						if (!pathMatch) {
							continue;
						}
						const [, locationIndex, dateField] = pathMatch;
						const cellField = dateField === 'dateFrom' ? 'from' : 'until';
						const cellId = `location_${Number(locationIndex) + 1}_${cellField}`;
						if (!errorMessageByCellId.has(cellId)) {
							errorMessageByCellId.set(cellId, message);
						}
					}
				}

				const updatedRow = { ...event.data };
				let rowChanged = false;
				for (
					let sequenceNumber = 0;
					sequenceNumber < locations.length;
					sequenceNumber += 1
				) {
					for (const dateField of ['from', 'until'] as const) {
						const cellId = `location_${sequenceNumber + 1}_${dateField}`;
						const cell = event.data[cellId];
						if (!cell) {
							continue;
						}
						const newErrorMessage = errorMessageByCellId.get(cellId);
						const cellHadError = cell.isValid === false;
						const cellHasDateValue = cell.value instanceof Date;

						if (
							newErrorMessage &&
							(cell.errorMessage !== newErrorMessage || !cellHadError)
						) {
							updatedRow[cellId] = {
								...cell,
								isValid: false,
								errorMessage: newErrorMessage,
							};
							rowChanged = true;
						} else if (!newErrorMessage && cellHadError && cellHasDateValue) {
							// Cell now has a value and no cross-location issue — clear the
							// stale error. Empty cells keep any "Required" error from submit.
							updatedRow[cellId] = {
								...cell,
								isValid: undefined,
								errorMessage: undefined,
							};
							rowChanged = true;
						}
					}
				}

				if (rowChanged) {
					event.api.applyTransaction({ update: [updatedRow] });
					event.api.redrawRows({ rowNodes: [event.node] });
				}
			},
			[validateLocationDates],
		);

		const validateContainerNumbers = useCallback(
			(event: CellValueChangedEvent) => {
				const warningMessage = t(
					'pages.bulk_create_shipments.container_duplicate_in_form',
					'This container number is duplicated in this form.',
				).toString();

				validateContainerNumberDuplicatesOnCellChange(event, warningMessage);
			},
			[t],
		);

		const saveColumnState = useCallback((event: ColumnEvent) => {
			let obj = {};
			event.api.getColumnDefs()?.forEach((colDef) => {
				if (!isColGroupDef(colDef)) {
					return;
				}

				obj = {
					...obj,
					[colDef.groupId as string]: colDef.children.map((column) => {
						if (!isColDef(column)) {
							return {};
						}

						return {
							key: column.colId?.replace(/locations.location_[0-9]*./, ''),
							hidden: !!column.hide,
						};
					}),
				};
			});

			localStorage.setItem('multiCreateViewColumns', JSON.stringify(obj));
		}, []);

		// biome-ignore lint/correctness/useExhaustiveDependencies: isPending stands for deps.
		const gridOptions: GridOptions = useMemo(
			() => ({
				// Column options
				defaultColDef: {
					editable: true,
					suppressHeaderMenuButton: true,
					suppressHeaderContextMenu: true,
					sortable: false,
					minWidth: 150,
					flex: 1,
					valueSetter: (params: ValueSetterParams) => {
						let { newValue } = params;
						// If the cell is empty and the column has a default value, reset it to default value instead
						if (!newValue || newValue === '') {
							if (params.column.getColId() in defaultRowData) {
								newValue = defaultRowData[params.column.getColId()].value;
							} else {
								newValue = undefined;
							}
						}

						let newData = {
							...params.data,
							[params.column.getColId()]: {
								value: newValue,
								isValid: undefined,
							},
						};

						// Column values using MultiSelect should always be of type number[].
						//
						// During fill operations, single selections become strings instead.
						// Multiple selections don't have this issue. The following transforms
						// it back to number[] to prevent the form  validation from failing
						// the schema.
						if (
							(params.column.getColId() === 'preferredCarriers' ||
								params.column.getColId() === 'requirements') &&
							typeof newValue === 'string'
						) {
							const validValue = [Number(newValue)];
							newData = {
								...params.data,
								[params.column.getColId()]: {
									value: validValue,
									isValid: undefined,
								},
							};
						}

						// Columns with side effects
						if (params.column.getColId() === 'containerType') {
							newData = {
								...params.data,
								[params.column.getColId()]: {
									value: newValue,
									isValid: undefined,
								},
								isoCode: {
									value: newValue?.isoCode,
									isValid: undefined,
								},
							};
						}

						if (params.column.getColId() === 'emptyContainer') {
							newData = {
								...params.data,
								[params.column.getColId()]: {
									value: newValue,
									isValid: undefined,
								},
								cargoGrossWeight: {
									value: {
										...params.data.cargoGrossWeight.value,
										quantity: newValue === true ? 0 : undefined,
									},
									isValid: undefined,
								},
							};
						}

						if (params.column.getColId() === 'cargoGrossWeight') {
							newData = {
								...params.data,
								[params.column.getColId()]: {
									value: newValue,
									isValid: undefined,
								},
								emptyContainer: {
									value: newValue?.quantity === 0,
									isValid: undefined,
								},
							};
						}

						if (params.column.getColId() === 'containerNumber') {
							newData = {
								...params.data,
								[params.column.getColId()]: {
									value: newValue?.toUpperCase(),
									isValid: undefined,
								},
							};
						}

						const locationRegex = /.*_location$/;
						if (params.column.getColId().match(locationRegex)) {
							const locations = {
								...Object.keys(params.data).reduce((result, key) => {
									if (locationRegex.test(key)) {
										result[key] = params.data[key].value;
									}
									return result;
								}, {}),
								[params.column.getColId()]: newValue,
							};

							newData = {
								...params.data,
								[params.column.getColId()]: {
									value: newValue,
									isValid: undefined,
								},
								termsAndConditions: {
									value: getTermsAndConditions(locations),
									isValid: undefined,
								},
							};
						}

						// Mirror manual create's auto-correct: when `from` moves past the
						// existing `until` for the same location, snap `until` up to the
						// new `from` so the location row stays internally consistent.
						const changedColumnId = params.column.getColId();
						if (
							matchLocationField(changedColumnId, 'from') &&
							newValue instanceof Date
						) {
							const untilColumnId = changedColumnId.replace('_from', '_until');
							const currentUntilValue = params.data[untilColumnId]?.value;
							if (
								currentUntilValue instanceof Date &&
								currentUntilValue < newValue
							) {
								newData = {
									...newData,
									[untilColumnId]: {
										value: newValue,
										isValid: undefined,
									},
								};
							}
						}

						/**
						 * If the row is no longer dirty, which means the user has removed all the data
						 * we reset the row state to to default row state
						 * and redraw the rows inside the grid to remove any validation indicators on that row
						 */
						const isRowDirty = isDirty(newData, defaultRowData);
						if (!isRowDirty) {
							const { id } = params.data;

							params.api.applyTransaction({
								update: [{ id, dirty: isRowDirty, ...defaultRowData }],
							});
							params.api.redrawRows();

							return true;
						}

						params.api.applyTransaction({
							update: [
								{
									...newData,
									dirty: isRowDirty,
								},
							],
						});

						return true;
					},
					valueGetter: (params: ValueGetterParams) =>
						params.data[params.column.getColId()]?.value,
					tooltipValueGetter: (params: ITooltipParams) => {
						const colId = params.column?.getColId();
						if (!colId) {
							return;
						}

						return (
							params.data[colId]?.errorMessage ??
							params.data[colId]?.warningMessage
						);
					},
					cellRenderer: (params: CustomCellRendererProps) => {
						const colId = params.column?.getColId();
						if (!colId) {
							return <span>{params.value}</span>;
						}

						const data = params.data[colId];
						return (
							<>
								<span className="pr-2">{params.value}</span>
								{data?.isValid === false && (
									<Icon
										className="absolute bottom-2/4 right-3 translate-y-2/4 text-red-500"
										icon={faExclamationCircle}
									/>
								)}
							</>
						);
					},
				},
				columnDefs: [
					{
						pinned: 'left',
						minWidth: 40,
						flex: 0,
						colId: 'rowNumber',
						headerName: '',
						valueGetter: 'node.rowIndex + 1',
						width: 40,
						cellClass: 'text-muted-foreground bg-muted text-center',
						suppressFillHandle: true,
						suppressPaste: true,
						resizeable: false,
						cellStyle: { 'padding-left': 0, 'padding-right': 0 },
						cellRenderer: (params: CustomCellRendererProps) => params.value,
					},
					...columns.map((column) => ({
						...column,
						marryChildren: true,
						children: column.children
							?.sort((a, b) => a.order - b.order)
							.map((childColumn) => ({
								...childColumn,
								headerComponent: requiredColumns.includes(childColumn.field)
									? () => (
											<span className="truncate">
												{childColumn.headerName}
												<span className="ml-1 text-red-500">*</span>
											</span>
										)
									: childColumn.headerName,
								...(childColumn.field === 'containerType' &&
									getSelectColumnDefaults(containerTypes)),
								...(childColumn.field === CONTAINER_NUMBER_FIELD && {
									cellRenderer: (params: CustomCellRendererProps) => {
										const colId = params.column?.getColId();
										if (!colId) {
											return <span>{params.value}</span>;
										}

										const data = params.data[colId];
										return (
											<>
												<span className="pr-2">{params.value}</span>
												{data?.isValid === false ? (
													<Icon
														className="absolute bottom-2/4 right-3 translate-y-2/4 text-red-500"
														icon={faExclamationCircle}
													/>
												) : (
													data?.isWarning === true && (
														<Icon
															className="absolute bottom-2/4 right-3 translate-y-2/4 text-orange-500"
															icon={faExclamationTriangle}
														/>
													)
												)}
											</>
										);
									},
								}),
								...(childColumn.field === 'preferredCarriers' &&
									getMultiSelectColumnDefaults(carriers ?? [])),
								...(childColumn.field === 'termsAndConditions' &&
									getSelectColumnDefaults(termsAndConditions)),
								...((childColumn.field === 'shippingLine' ||
									matchLocationField(childColumn.field, 'shippingLine')) &&
									getSelectColumnDefaults(shippingLines)),
								...(childColumn.field === 'cargoGrossWeight' && {
									cellEditor: WeightCellEditor,
									cellRenderer: (params: CustomCellRendererProps) => {
										const colId = params.column?.getColId();
										if (!colId) {
											return (
												params.value?.quantity !== undefined && (
													<span>
														{formatWeight(
															params.value.quantity,
															params.value.unit,
														)}
													</span>
												)
											);
										}

										const data = params.data[colId];
										return (
											<>
												{params.value?.quantity && (
													<span className="pr-2">
														{formatWeight(
															params.value.quantity,
															params.value.unit,
														)}
													</span>
												)}
												{data?.isValid === false && (
													<Icon
														className="absolute bottom-2/4 right-3 translate-y-2/4 text-red-500"
														icon={faExclamationCircle}
													/>
												)}
											</>
										);
									},
								}),
								...(childColumn.field === 'requirements' &&
									getMultiSelectColumnDefaults(requirements)),
								...(childColumn.field === 'price' && {
									cellEditor: CurrencyCellEditor,
									cellRenderer: (params: CustomCellRendererProps) => {
										const colId = params.column?.getColId();
										if (!colId) {
											return (
												params.value?.quantity && (
													<span>
														{formatPrice(
															params.value.quantity,
															params.value.currency,
														)}
													</span>
												)
											);
										}

										const data = params.data[colId];
										return (
											<>
												{params.value?.quantity && (
													<span className="pr-2">
														{formatPrice(
															params.value.quantity,
															params.value.currency,
														)}
													</span>
												)}
												{data?.isValid === false && (
													<Icon
														className="absolute bottom-2/4 right-3 translate-y-2/4 text-red-500"
														icon={faExclamationCircle}
													/>
												)}
											</>
										);
									},
								}),
								...(matchLocationField(childColumn.field, 'location') &&
									getLocationAutoCompleteDefaults()),
								...(matchLocationField(childColumn.field, 'type') && {
									...getSelectColumnDefaults(locationTypes),
									suppressFillHandle: true,
									editable: false,
									lockVisible: true,
								}),
								...((matchLocationField(childColumn.field, 'from') ||
									matchLocationField(childColumn.field, 'until')) && {
									minWidth: 200,
									cellEditor: (params: CustomCellEditorProps) => (
										<DateTimePickerEditor {...params} />
									),
									cellRenderer: (params: CustomCellRendererProps) => {
										const colId = params.column?.getColId();
										if (!colId) {
											return (
												<span>
													{params.value ? (
														formatDatetime(params.value)
													) : (
														<span className="text-muted-foreground">
															{DATETIME_HINT}
														</span>
													)}
												</span>
											);
										}

										const data = params.data[colId];
										return (
											<>
												<span className="pr-2">
													{params.value ? (
														formatDatetime(params.value)
													) : (
														<span className="text-muted-foreground">
															{DATETIME_HINT}
														</span>
													)}
												</span>
												{data?.isValid === false && (
													<Icon
														className="absolute bottom-2/4 right-3 translate-y-2/4 text-red-500"
														icon={faExclamationCircle}
													/>
												)}
											</>
										);
									},
								}),
								...((childColumn.field === 'vat' ||
									childColumn.field === 'generatorSet' ||
									childColumn.field === 'acceptancePrice' ||
									childColumn.field === 'emptyContainer' ||
									childColumn.field === 'bidOnly') && {
									cellDataType: 'boolean',
								}),
							})),
					})),
					/**
					 * Any data we want available in the grid has to be a column
					 * using hide: true and suppressColumnsToolPanel: true will hide the column and remove it from the columns tool panel
					 */
					{ field: 'dirty', hide: true, suppressColumnsToolPanel: true },
				],
				// Row options
				rowData: [...new Array(GRID_ROW_COUNT)].map((_, i) => ({
					id: i + 1,
					dirty: false,
					...defaultRowData,
				})),
				getRowId: (row) => row.data.id.toString(),
				onCellValueChanged: (event: CellValueChangedEvent) => {
					validateRowDatesOnCellChange(event);
					validateContainerNumbers(event);
				},
				onCellClicked: (params) => {
					if (params.column.getColId() === 'rowNumber') {
						const visibleColumns = params.api.getAllDisplayedColumns();

						params.api.clearRangeSelection();
						params.api.addCellRange({
							rowStartIndex: params.node.rowIndex,
							rowEndIndex: params.node.rowIndex,
							columnStart: visibleColumns[1].getColId(),
							columnEnd: visibleColumns[visibleColumns.length - 1].getColId(),
						});
					}
				},

				autoSizeStrategy: {
					type: 'fitCellContents',
				},
				// Cell options
				stopEditingWhenCellsLoseFocus: true,
				enableRangeSelection: true,
				enableFillHandle: true,
				fillHandleDirection: 'y',
				suppressContextMenu: true,
				tooltipShowDelay: 500,
				// Sidepanel options
				sideBar: {
					toolPanels: [
						{
							id: 'columns',
							labelDefault: t('shipments.table.sidebar.columns', {
								defaultValue: 'Columns',
							}),
							labelKey: 'columns',
							iconKey: 'columns',
							toolPanel: 'agColumnsToolPanel',
							toolPanelParams: {
								suppressRowGroups: true,
								suppressValues: true,
								suppressPivots: true,
								suppressPivotMode: true,
								suppressColumnFilter: true,
								suppressColumnSelectAll: true,
								suppressColumnExpandAll: true,
							},
						},
					],
				},
				// Clipboard options
				processCellFromClipboard: ({ value, column, ...rest }) => {
					const handleProcessCellFromClipboard = (
						valueToCheck: string,
						columnToCheck: string,
					) => {
						if (
							matchLocationField(columnToCheck, 'from') ||
							matchLocationField(columnToCheck, 'until')
						) {
							if (!valueToCheck) {
								return null;
							}

							const date = dayjs(
								valueToCheck,
								[
									'DD.MM.YYYY HH:mm',
									'DD.MM.YYYY',
									'D.M.YYYY HH:mm',
									'D.M.YYYY',
									'DD-MM-YYYY HH:mm',
									'DD-MM-YYYY',
									'D-M-YYYY HH:mm',
									'D-M-YYYY',
									'DD MM YYYY HH:mm',
									'DD MM YYYY',
									'D M YYYY HH:mm',
									'D M YYYY',
									'MM/DD/YYYY HH:mm',
									'MM/DD/YYYY',
									'M/D/YYYY HH:mm',
									'M/D/YYYY',
									'YYYY-MM-DD HH:mm',
									'YYYY-MM-DD',
									'YYYY-M-D HH:mm',
									'YYYY-M-D',
								],
								false,
							);

							if (!date.isValid()) {
								return null;
							}

							return date.toDate();
						}

						if (columnToCheck === 'cargoGrossWeight') {
							try {
								const valueToCheckParsed = JSON.parse(valueToCheck);
								return {
									...defaultRowData[columnToCheck].value,
									quantity:
										'quantity' in valueToCheckParsed
											? Number(valueToCheckParsed.quantity)
											: undefined,
									unit:
										'unit' in valueToCheckParsed
											? valueToCheckParsed.unit
											: undefined,
								};
							} catch {
								return {
									...defaultRowData[columnToCheck].value,
									quantity: valueToCheck ? Number(valueToCheck) : undefined,
								};
							}
						}

						if (columnToCheck === 'price') {
							try {
								const valueToCheckParsed = JSON.parse(valueToCheck);
								return {
									...defaultRowData[columnToCheck].value,
									quantity:
										'quantity' in valueToCheckParsed
											? Number(valueToCheckParsed.quantity)
											: undefined,
									currency:
										'currency' in valueToCheckParsed
											? valueToCheckParsed.currency
											: undefined,
								};
							} catch {
								return {
									...defaultRowData[columnToCheck].value,
									quantity: valueToCheck ? Number(valueToCheck) : undefined,
								};
							}
						}

						if (columnToCheck === 'containerType') {
							return (
								valueToCheck &&
								containerTypes.find(
									(containerType) =>
										containerType.label.toLowerCase() === valueToCheck ||
										containerType.isoCode.toLowerCase() === valueToCheck,
								)
							);
						}

						if (columnToCheck === 'shippingLine') {
							return shippingLines.find(
								(shippingLine) =>
									shippingLine.label.toLowerCase() === valueToCheck ||
									shippingLine.scacCode?.toLowerCase() === valueToCheck,
							);
						}

						// Receiving comma separated labels from external excel or something.
						if (columnToCheck === 'requirements') {
							const valueArr = valueToCheck.replace(' ', '').split(',');

							return valueArr.map((val) =>
								requirements.find(
									(requirement) =>
										requirement.label.toLowerCase() === val.toLowerCase(),
								),
							);
						}

						if (columnToCheck === 'containerNumber') {
							return valueToCheck.replace(/[^a-zA-Z0-9]/g, '');
						}

						return valueToCheck;
					};

					try {
						let parsedValue;
						if (
							column.getColId() === 'cargoGrossWeight' ||
							column.getColId() === 'price'
						) {
							parsedValue = JSON.parse(value.replace(/[.,\s]/g, ''));
						} else {
							parsedValue = JSON.parse(value);
						}

						if (
							typeof parsedValue !== 'string' &&
							typeof parsedValue !== 'number'
						) {
							return parsedValue;
						}

						return handleProcessCellFromClipboard(
							parsedValue.toString(),
							column.getColId(),
						);
					} catch {
						return handleProcessCellFromClipboard(value, column.getColId());
					}
				},
				processCellForClipboard: (params) => {
					if (params.value instanceof Date) {
						return dayjs(params.value).format('YYYY-MM-DD, HH:mm');
					}

					if (typeof params.value === 'object') {
						return JSON.stringify(params.value);
					}

					return params.value;
				},
				suppressLastEmptyLineOnPaste: true,
				onColumnVisible: saveColumnState,
				onColumnMoved: (event) => {
					if (!event.finished) {
						return;
					}

					saveColumnState(event);
				},
			}),
			[isPending],
		);

		return (
			<div className="h-[calc(100dvh_-_275px)] w-full">
				<AgGridReact
					{...gridOptions}
					ref={ref}
					className="ag-theme-quartz align-baseline [&_.ag-cell-inline-editing_.relative]:h-full [&_.ag-cell-inline-editing_:is(input,button)]:h-full [&_.ag-cell-inline-editing_:is(input,button)]:border-transparent [&_.ag-cell-inline-editing_:is(input,button)]:ring-0 [&_.ag-cell-inline-editing_:is(input,button)]:ring-offset-0"
				/>
			</div>
		);
	},
);

MultiCreateTable.displayName = 'MultiCreateTable';

export default MultiCreateTable;
